import React, { useState, useEffect } from 'react';
import {
  Users, Search, ShieldCheck, ShieldOff, Save,
} from 'lucide-react';
import { usersApi } from '../api/client';
import { fmtDate, fmtRelative } from '../utils/format';
import { useAuth } from '../auth/AuthContext';

export default function UserManagement() {
  const { user: me, hasPermission } = useAuth();
  const canManage = hasPermission('users:manage');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingRole, setPendingRole] = useState({}); // { userId: role_id }

  const load = () => {
    setLoading(true);
    Promise.all([usersApi.list({ page_size: 100 }), usersApi.roles()])
      .then(([list, rs]) => {
        setUsers(list.items || []);
        setRoles(rs || []);
      })
      .catch((e) => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = !search ? users : users.filter((u) => {
    const s = search.toLowerCase();
    return (u.email || '').toLowerCase().includes(s) || (u.name || '').toLowerCase().includes(s);
  });

  const updateRole = async (userId, role_id) => {
    try {
      await usersApi.setRole(userId, role_id);
      setPendingRole((s) => ({ ...s, [userId]: undefined }));
      load();
    } catch (e) { alert(e?.response?.data?.error || 'Could not change role.'); }
  };
  const toggleActive = async (u) => {
    if (u.id === me?.id) { alert('You cannot deactivate yourself.'); return; }
    try {
      await usersApi.setActive(u.id, !u.is_active);
      load();
    } catch (e) { alert(e?.response?.data?.error || 'Could not toggle.'); }
  };

  return (
    <div>
      <div className="t-eyebrow">SRVC · Console Access</div>
      <h1 className="t-display" style={{ fontSize: 38, marginBottom: 8 }}>
        <Users size={26} style={{ verticalAlign: 'middle', marginRight: 10, color: 'var(--saffron-deep)' }} />
        Users & roles.
      </h1>
      <p style={{ color: 'var(--ink-600)', fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 15, marginBottom: 24 }}>
        Who has access, and to which capabilities.
      </p>

      <div className="panel" style={{ padding: '12px 16px', marginBottom: 16 }}>
        <div style={{ position: 'relative', maxWidth: 360 }}>
          <Search size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-400)' }} />
          <input className="input" style={{ paddingLeft: 34 }}
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {error && <div className="panel" style={{ padding: '12px 16px', borderColor: 'var(--red)', background: 'var(--red-tint)', color: 'var(--red)', marginBottom: 16 }}>{error}</div>}

      <div className="panel" style={{ overflow: 'hidden' }}>
        <table className="table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Last login</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32 }}>
                <span className="loader" style={{ verticalAlign: 'middle' }} />
              </td></tr>
            )}
            {!loading && !filtered.length && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--ink-400)', fontFamily: 'var(--serif)', fontStyle: 'italic' }}>
                No users found.
              </td></tr>
            )}
            {!loading && filtered.map((u) => {
              const isMe = u.id === me?.id;
              const draftRole = pendingRole[u.id];
              const currentRoleObj = roles.find((r) => r.name === u.role);
              const currentRoleId = currentRoleObj?.id;
              const dirty = draftRole && draftRole !== currentRoleId;

              return (
                <tr key={u.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {u.picture ? (
                        <img src={u.picture} alt="" style={{ width: 32, height: 32, borderRadius: '50%' }} />
                      ) : (
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%',
                          background: 'var(--saffron-light)', color: 'var(--saffron-deep)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 600, fontSize: 12,
                        }}>
                          {(u.name || u.email).slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 13.5 }}>
                          {u.name || '—'}
                          {isMe && <span style={{ marginLeft: 6, color: 'var(--saffron-deep)', fontSize: 11, fontWeight: 600 }}>(you)</span>}
                        </div>
                        <div style={{ color: 'var(--ink-400)', fontSize: 11.5 }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {canManage && !isMe ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <select
                          className="select"
                          style={{ width: 160, padding: '6px 10px', fontSize: 12 }}
                          value={draftRole || currentRoleId || ''}
                          onChange={(e) => setPendingRole((s) => ({ ...s, [u.id]: e.target.value }))}
                        >
                          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                        {dirty && (
                          <button
                            onClick={() => updateRole(u.id, draftRole)}
                            className="btn btn-primary"
                            style={{ padding: '6px 10px', fontSize: 11 }}
                          ><Save size={11} /> Save</button>
                        )}
                      </div>
                    ) : (
                      <span className="badge badge-saffron">{u.role}</span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--ink-600)' }}>
                    {u.last_login_at ? (
                      <>
                        <div>{fmtRelative(u.last_login_at)}</div>
                        <div style={{ fontSize: 10.5, color: 'var(--ink-400)' }}>{fmtDate(u.last_login_at)}</div>
                      </>
                    ) : <span style={{ color: 'var(--ink-400)' }}>Never</span>}
                  </td>
                  <td>
                    {u.is_active
                      ? <span className="badge badge-green">Active</span>
                      : <span className="badge badge-red">Disabled</span>}
                  </td>
                  <td>
                    {canManage && !isMe && (
                      <button
                        className="btn"
                        onClick={() => toggleActive(u)}
                        style={{
                          padding: '6px 10px', fontSize: 12,
                          color: u.is_active ? 'var(--red)' : 'var(--green)',
                          borderColor: u.is_active ? 'var(--red-tint)' : 'var(--green-tint)',
                        }}
                      >
                        {u.is_active ? (
                          <><ShieldOff size={12} /> Deactivate</>
                        ) : (
                          <><ShieldCheck size={12} /> Activate</>
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
